/* Команды записи.
 *
 * Одна команда — один вызов сервера с явным смыслом, а не PATCH произвольных
 * полей. Успех означает, что изменение уже в базе; экран после этого не
 * «дорисовывает» результат, а перечитывает затронутые ресурсы.
 *
 * Оптимистичных обновлений здесь намеренно нет. Почти всё, что видит
 * пользователь в списке, выводится на сервере — счётчики, консолидированные
 * коды, переходы жизненного цикла, — и клиент не может предсказать строку
 * после команды. Пока команда идёт, кнопка показывает занятость; дальше
 * работает перечитывание.
 */
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { request } from './api'

type Method = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface CommandSpec<TArgs, TResult> {
  method?: Method
  path: (args: TArgs) => string
  body?: (args: TArgs) => unknown
  /** Ресурсы, состояние которых команда меняет. Сбрасываются целиком: списки
      постраничные, и точечная правка кеша разошлась бы с сервером. */
  invalidate: (args: TArgs, result: TResult) => readonly QueryKey[]
}

export function useCommand<TArgs, TResult = unknown>(spec: CommandSpec<TArgs, TResult>) {
  const client = useQueryClient()
  return useMutation<TResult, Error, TArgs>({
    mutationFn: (args) =>
      request<TResult>(spec.method ?? 'POST', spec.path(args), spec.body?.(args), {
        /* Новый ключ на каждое нажатие, но один на все повторы этого нажатия:
           повтор из-за обрыва связи обязан вернуть прежний результат, а не
           выполниться второй раз. */
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: async (result, args) => {
      await Promise.all(
        spec.invalidate(args, result).map((queryKey) => client.invalidateQueries({ queryKey })),
      )
    },
  })
}
